<template>
  <div class="contact-page">
    <!-- Hero Section -->
    <div class="hero-section">
      <div class="hero-background"></div>
      <div class="hero-overlay"></div>
      <div class="hero-gradient-overlay"></div>
      <v-container class="hero-content">
        <div class="hero-text-container">
          <h1 class="hero-title">{{ $t('contact.title') }}</h1>
          <div class="hero-subtitle">{{ $t('contact.heroSubtitle') }}</div>
        </div>
      </v-container>
    </div>

    <!-- Core Values Section -->
    <div class="values-section py-12">
      <v-container class="values-container" style="max-width: 1400px;">
        <!-- Core Values Header -->
        <div class="values-header text-center mb-10">
          <h3 class="text-h4 font-weight-bold primary--text mb-3">{{ $t('about.coreValuesTitle') }}</h3>
          <div class="values-divider-container">
            <div class="values-divider-line"></div>
            <div class="values-divider-circle">
              <v-icon color="primary" size="24">mdi-star</v-icon>
            </div>
            <div class="values-divider-line"></div>
          </div>
          <p class="values-subtitle mt-6">{{ $t('contact.coreValuesIntro') }}</p>
        </div>

        <!-- Core Values Cards in Staggered Layout -->
        <div class="values-cards-container">
          <!-- Integrity -->
          <div class="value-card integrity-card app-card">
            <div class="value-card-inner">
              <div class="value-icon-wrapper integrity">
                <v-icon size="36" color="white">mdi-shield-check</v-icon>
              </div>
              <h4 class="value-title">{{ $t('about.integrityTitle') }}</h4>
              <div class="value-divider"></div>
              <p class="value-desc">{{ $t('about.integrityDesc') }}</p>
            </div>
          </div>

          <!-- Customer First -->
          <div class="value-card customer-card app-card">
            <div class="value-card-inner">
              <div class="value-icon-wrapper customer">
                <v-icon size="36" color="white">mdi-account-heart</v-icon>
              </div>
              <h4 class="value-title">{{ $t('about.customerFirstTitle') }}</h4>
              <div class="value-divider"></div>
              <p class="value-desc">{{ $t('about.customerFirstDesc') }}</p>
            </div>
          </div>

          <!-- Win-Win -->
          <div class="value-card cooperation-card app-card">
            <div class="value-card-inner">
              <div class="value-icon-wrapper cooperation">
                <v-icon size="36" color="white">mdi-handshake</v-icon>
              </div>
              <h4 class="value-title">{{ $t('about.cooperationTitle') }}</h4>
              <div class="value-divider"></div>
              <p class="value-desc">{{ $t('about.cooperationDesc') }}</p>
            </div>
          </div>

          <!-- Perseverance -->
          <div class="value-card perseverance-card app-card">
            <div class="value-card-inner">
              <div class="value-icon-wrapper perseverance">
                <v-icon size="36" color="white">mdi-rocket-launch</v-icon>
              </div>
              <h4 class="value-title">{{ $t('about.perseveranceTitle') }}</h4>
              <div class="value-divider"></div>
              <p class="value-desc">{{ $t('about.perseveranceDesc') }}</p>
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <div class="bg-white">
      <!-- 联系我们部分 -->
      <ContactSection :is-dark="false" background-image="/images/contact.svg" />
    </div>

  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import ContactSection from '@/components/ContactSection.vue';

const { t } = useI18n();

// 公司位置信息（仅作为参考，不再用于动态地图）
const locations = {
  headquarters: {
    lat: 31.2989,  // 苏州工业园区坐标
    lng: 120.6668
  },
  factory: {
    lat: 36.6520,  // 潍坊坊子区坐标
    lng: 119.2009
  }
};
</script>

<style scoped>
.contact-page {
  background: #ffffff;
}

/* Core Values Section Styles */
.values-section {
  position: relative;
  overflow: hidden;
}

.values-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2337b7a5' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.5;
}

.values-container {
  position: relative;
  z-index: 1;
}

.values-header {
  position: relative;
}

.values-subtitle {
  color: #666;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
}

.values-divider-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px auto;
  width: 100%;
  max-width: 400px;
}

.values-divider-line {
  height: 2px;
  flex: 1;
  background: linear-gradient(90deg, transparent, rgba(3, 55, 135, 0.5));
}

.values-divider-line:last-child {
  background: linear-gradient(90deg, rgba(3, 55, 135, 0.5), transparent);
}

.values-divider-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  box-shadow: 0 0 0 2px rgba(3, 55, 135, 0.3);
  margin: 0 15px;
}

.values-cards-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 40px;
}

.value-card {
  position: relative;
  height: 100%;
  transition: all 0.4s ease;
}

.value-card-inner {
  background-color: white;
  border-radius: 16px;
  padding: 30px 25px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  z-index: 1;
}




.value-card:hover .value-card-inner {
  box-shadow: 0 15px 35px rgba(3, 55, 135, 0.15);
}

.value-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  transition: all 0.4s ease;
  box-shadow: 0 8px 20px rgba(3, 55, 135, 0.2);
  transform: rotate(45deg);
}

.value-icon-wrapper .v-icon {
  transform: rotate(-45deg);
}

.value-card:hover .value-icon-wrapper {
  transform: rotate(0deg);
  border-radius: 50%;
}

.value-card:hover .value-icon-wrapper .v-icon {
  transform: rotate(0deg) scale(1.2);
}

.value-icon-wrapper.integrity {
  background: linear-gradient(135deg, rgba(3, 55, 135, 0.9) 0%, rgba(12, 72, 162, 0.9) 100%);
}

.value-icon-wrapper.customer {
  background: linear-gradient(135deg, rgba(3, 55, 135, 0.9) 20%, rgba(3, 55, 135, 0.8) 100%);
}

.value-icon-wrapper.cooperation {
  background: linear-gradient(135deg, rgba(12, 72, 162, 0.9) 0%, rgba(22, 71, 148, 0.9) 100%);
}

.value-icon-wrapper.perseverance {
  background: linear-gradient(135deg, rgba(22, 71, 148, 0.9) 0%, rgba(3, 55, 135, 0.8) 100%);
}

.value-title {
  color: #033787;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.value-divider {
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, rgba(3, 55, 135, 0.3), rgba(12, 72, 162, 0.3));
  margin: 0 auto 15px;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.value-card:hover .value-divider {
  width: 60px;
  background: linear-gradient(90deg, rgba(3, 55, 135, 0.8), rgba(12, 72, 162, 0.8));
}

.value-desc {
  color: #555;
  font-size: 1rem;
  line-height: 1.6;
  flex-grow: 1;
}
/* Hero Section Styles */
.hero-section {
  position: relative;
  height: 600px;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  min-width: 100%;
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  height: 100%;
  background: url('/images/contact.jpg') no-repeat center center;
  background-size: cover;
  filter: brightness(0.85) contrast(1.05) saturate(0.9);
  z-index: 1;
}

/* 两侧模糊效果 */
.hero-background::after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120vw; /* 比实际宽度多20%，确保两侧有足够空间 */
  height: 100%;
  background: url('/images/contact.jpg') no-repeat center center;
  background-size: cover;
  filter: blur(20px) brightness(0.7) saturate(0.8);
  z-index: 0;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 2;
}

.hero-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.3) 40%,
    rgba(0, 0, 0, 0.1) 60%,
    rgba(0, 0, 0, 0.4) 100%
  );
  z-index: 3;
}

.hero-content {
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.hero-text-container {
  max-width: 800px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.hero-title {
  color: white;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 1px;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.5rem;
  font-weight: 400;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
/* 地图样式 */
.map-wrapper {
  position: relative;
  height: 700px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
}

.static-map-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.static-map-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.location-cards-container {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(var(--v-theme-primary), 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 10;
}

.location-cards {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.location-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  width: 100%;
}

.location-card:hover {
  transform: translateY(-4px);
  border-color: rgba(var(--v-theme-primary), 0.3);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.location-card-header {
  padding: 24px;
  text-align: center;
  background: rgba(var(--v-theme-primary), 0.05);
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.1);
  position: relative;
}

.location-card-header::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--v-primary-base), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.location-card:hover .location-card-header::after {
  opacity: 1;
}

.location-card-content {
  padding: 24px;
}

.info-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.info-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item a {
  color: inherit;
  text-decoration: none;
  transition: color 0.3s ease;
}

.info-item:hover a {
  color: rgb(var(--v-theme-primary));
}

.info-item .v-icon {
  transition: transform 0.3s ease;
}

.info-item:hover .v-icon {
  transform: scale(1.1);
}

.business-hours {
  flex: 1;
}

.business-hours div {
  margin-bottom: 4px;
  transition: color 0.3s ease;
}

.business-hours div:last-child {
  margin-bottom: 0;
}

.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6);
}

.map-controls {
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.map-controls .v-btn {
  background: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.map-controls .v-btn:hover {
  transform: scale(1.05);
}

/* FAQ样式 */
.faq-section {
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.v-expansion-panels {
  background: transparent;
}

.v-expansion-panel {
  background: white;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  transition: all 0.3s ease;
}

.v-expansion-panel:hover {
  border-color: rgba(var(--v-theme-primary), 0.3);
  transform: translateY(-2px);
}

.v-expansion-panel-title {
  font-weight: 500;
}

/* FAQ 动画样式 */
.animate-title {
  animation: slideDown 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(-20px);
}

.animate-subtitle {
  animation: slideDown 0.6s ease-out 0.2s forwards;
  opacity: 0;
  transform: translateY(-20px);
}

.faq-panel {
  animation: slideUp 0.6s ease-out forwards;
  animation-delay: var(--animation-delay);
  opacity: 0;
  transform: translateY(20px);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* FAQ 面板整体样式 */
.faq-panels {
  --panel-transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-panel {
  transition: all var(--panel-transition);
  position: relative;
  overflow: hidden;
  margin-bottom: 16px !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.1) !important;
}

/* 面板标题样式 */
:deep(.v-expansion-panel-title) {
  min-height: 64px;
  padding: 16px 24px;
  transition: all var(--panel-transition);
}

:deep(.v-expansion-panel-title__overlay) {
  background: rgba(var(--v-theme-primary), 0.04) !important;
  opacity: 0 !important;
  transition: opacity var(--panel-transition) !important;
}

:deep(.v-expansion-panel--active) .v-expansion-panel-title__overlay {
  opacity: 1 !important;
}

:deep(.v-expansion-panel-title--active) {
  background: linear-gradient(
      to right,
      rgba(var(--v-theme-primary), 0.08),
      rgba(var(--v-theme-primary), 0.04)
  );
  min-height: 72px;
}

:deep(.v-expansion-panel-title--active::before) {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: rgb(var(--v-theme-primary));
  opacity: 1;
  transform: scaleY(0.8);
  transition: transform var(--panel-transition);
}

.panel-title-content {
  position: relative;
  z-index: 1;
  transition: all var(--panel-transition);
}

/* 展开状态样式 */
:deep(.v-expansion-panel--active) {
  margin: 24px 0 !important;
  border-color: rgba(var(--v-theme-primary), 0.2) !important;
  box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.08) !important;
  background: linear-gradient(
      to right bottom,
      rgba(var(--v-theme-primary), 0.02),
      rgba(var(--v-theme-primary), 0.01)
  ) !important;
}

:deep(.v-expansion-panel--active) .question-icon {
  opacity: 0;
  transform: translate(-50%, -50%) rotate(180deg) scale(0);
}

:deep(.v-expansion-panel--active) .answer-icon {
  opacity: 1;
  transform: translate(-50%, -50%) rotate(0) scale(1);
}

:deep(.v-expansion-panel--active) .panel-title {
  color: rgb(var(--v-theme-primary));
  transform: translateX(4px);
  font-weight: 600;
}

/* 答案内容样式 */
.answer-content {
  position: relative;
  padding: 0 24px 0 64px;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: 0.1s;
}

.answer-wrapper {
  position: relative;
  padding: 16px 0;
}

.answer-wrapper::before {
  content: '';
  position: absolute;
  left: -40px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
      to bottom,
      rgb(var(--v-theme-primary)) 0%,
      rgba(var(--v-theme-primary), 0.5) 50%,
      rgba(var(--v-theme-primary), 0.1) 100%
  );
  transform: scaleY(0);
  transform-origin: top;
  animation: scaleIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: 0.2s;
}

/* 悬浮效果 */
.faq-panel:not(.v-expansion-panel--active):hover {
  border-color: rgba(var(--v-theme-primary), 0.2) !important;
  transform: translateX(4px);
  background: linear-gradient(
      to right,
      rgba(var(--v-theme-primary), 0.02),
      transparent
  ) !important;
}

.faq-panel:not(.v-expansion-panel--active):hover .panel-title {
  color: rgb(var(--v-theme-primary));
}

.faq-panel:not(.v-expansion-panel--active):hover .icon-wrapper {
  transform: scale(1.1);
}

/* 图标样式 */
.icon-wrapper {
  position: relative;
  width: 24px;
  height: 24px;
  margin-right: 16px;
  transition: transform var(--panel-transition);
}

.question-icon,
.answer-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: all var(--panel-transition);
  filter: drop-shadow(0 2px 4px rgba(var(--v-theme-primary), 0.2));
}

.question-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

.answer-icon {
  opacity: 0;
  transform: translate(-50%, -50%) rotate(-180deg) scale(0);
  color: rgb(var(--v-theme-primary)) !important;
  background: white;
  border-radius: 50%;
}

:deep(.v-expansion-panel--active) .answer-icon {
  opacity: 1;
  transform: translate(-50%, -50%) rotate(0) scale(1);
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.2);
}

/* 悬浮效果增强 */
.faq-panel:not(.v-expansion-panel--active):hover .question-icon {
  transform: translate(-50%, -50%) scale(1.1);
  filter: drop-shadow(0 4px 8px rgba(var(--v-theme-primary), 0.3));
}

/* 动画关键帧 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}

/* 响应式样式 */
@media (max-width: 960px) {
  .map-wrapper {
    height: 800px;
  }

  .map-container {
    flex-direction: column;
  }

  .static-map-container {
    height: 400px;
  }

  .location-cards-container {
    position: relative;
    width: 100%;
    height: 400px;
    padding: 24px;
    background: #ffffff;
    border-left: none;
    border-top: 1px solid rgba(var(--v-theme-primary), 0.1);
  }

  .location-cards {
    gap: 16px;
  }
}

@media (max-width: 960px) {
  .values-cards-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .value-icon-wrapper {
    width: 70px;
    height: 70px;
    margin-bottom: 20px;
  }

  .value-icon-wrapper .v-icon {
    font-size: 28px !important;
  }

  .value-title {
    font-size: 1.1rem !important;
    margin-bottom: 12px !important;
  }

  .value-desc {
    font-size: 0.9rem !important;
    line-height: 1.5;
  }
  
  .map-wrapper {
    height: 800px;
  }

  .map-container {
    flex-direction: column;
  }

  .static-map-container {
    height: 400px;
  }

  .location-cards-container {
    position: relative;
    width: 100%;
    height: 400px;
    padding: 24px;
    background: #ffffff;
    border-left: none;
    border-top: 1px solid rgba(var(--v-theme-primary), 0.1);
  }

  .location-cards {
    gap: 16px;
  }


@media (max-width: 600px) {
  .values-cards-container {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .values-subtitle {
    font-size: 0.95rem;
    padding: 0 15px;
  }
  
  .value-card-inner {
    padding: 25px 20px;
  }
  
  .value-card::before {
    top: 6px;
    left: 6px;
    right: 6px;
  }
  
  .value-icon-wrapper {
    width: 60px;
    height: 60px;
    margin-bottom: 15px;
    border-radius: 15px;
  }

  .value-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .value-title {
    font-size: 1rem !important;
    margin-bottom: 8px !important;
  }

  .value-desc {
    font-size: 0.85rem !important;
  }
  
  .hero-section {
    height: 500px;
  }
  
  .hero-text-container {
    padding: 20px;
  }
  
  .hero-title {
    font-size: 2.2rem;
    margin-bottom: 12px;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
  }
}
  
  .map-wrapper {
    height: 900px;
  }

  .static-map-container {
    height: 300px;
  }

  .location-cards-container {
    height: 600px;
    padding: 16px;
  }

  .location-card-header {
    padding: 16px;
  }

  .location-card-content {
    padding: 16px;
  }

  .answer-content {
    padding-left: 24px;
  }

  .icon-wrapper {
    width: 20px;
    height: 20px;
  }

  .panel-title {
    font-size: 0.9rem;
  }
}

/* 地图弹窗样式 */
:deep(.custom-popup) {
  margin-bottom: 8px;
}

:deep(.custom-popup .leaflet-popup-content-wrapper) {
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

:deep(.custom-popup .leaflet-popup-content) {
  margin: 16px;
}

:deep(.custom-popup .map-popup h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--v-primary-base);
}

:deep(.custom-popup .map-popup p) {
  margin: 0;
  color: #666;
}
</style>
